import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import './AdminLogin.css';

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 120000;

const AdminLogin = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(0);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);

  React.useEffect(() => {
    if (lockoutUntil <= Date.now()) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, lockoutUntil - Date.now());
      setLockoutRemaining(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        setError('');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const isLockedOut = lockoutUntil > Date.now();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLockedOut) {
      setError(`Account locked. Try again in ${Math.ceil(lockoutRemaining / 1000)}s`);
      return;
    }

    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setFailedAttempts(0);
      setLockoutUntil(0);
      onLoginSuccess();
    } catch (err) {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        const lockUntil = Date.now() + LOCKOUT_DURATION_MS;
        setLockoutUntil(lockUntil);
        setLockoutRemaining(LOCKOUT_DURATION_MS);
        setError(`Too many failed attempts. Locked for ${LOCKOUT_DURATION_MS / 1000} seconds`);
        setFailedAttempts(0);
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later');
      } else {
        const attemptsLeft = MAX_ATTEMPTS - newAttempts;
        setError(`Invalid credentials. ${attemptsLeft} attempt${attemptsLeft !== 1 ? 's' : ''} remaining`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <motion.div
        className="login-box"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <div className="login-header">
          <h1>MTS</h1>
          <h2>Admin Access</h2>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="email@mtstechsolutions.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="password"
            />
          </div>

          <button type="submit" className="btn-login" disabled={loading || isLockedOut}>
            {isLockedOut ? (
              `Locked (${Math.ceil(lockoutRemaining / 1000)}s)`
            ) : loading ? (
              <span className="btn-loading">
                <span className="login-spinner" />
                Authenticating...
              </span>
            ) : 'Login'}
          </button>
        </form>

        <div className="login-footer">Authorized personnel only</div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
